import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  BarChart3,
  FileText,
  Home,
  Settings,
  Users,
  ShieldCheck,
  Award,
  Layout,
  BookOpen,
  Star,
  Percent,
  MessageSquare,
  Mail,
} from 'lucide-react';

interface SidebarLinkProps {
  href: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  active?: boolean;
}

function SidebarLink({ href, icon, children, active }: SidebarLinkProps) {
  return (
    <Link
      to={href}
      className={cn(
        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all",
        active
          ? "bg-primary text-primary-foreground"
          : "text-muted-foreground hover:text-foreground hover:bg-accent"
      )}
    >
      {icon}
      <span>{children}</span>
    </Link>
  );
}

export function AdminSidebar() {
  const location = useLocation();
  const currentPath = location.pathname;
  
  const isActive = (path: string) => {
    if (path === '/' || path === '/dashboard') {
      return currentPath === '/' || currentPath === '/dashboard';
    }
    return currentPath.startsWith(path);
  };

  return (
    <div className="flex h-full w-64 flex-col border-r bg-background">
      <div className="flex h-14 items-center border-b px-4">
        <Link to="/dashboard" className="flex items-center">
          <img 
            src="/lovable-uploads/6055e260-5182-4f1b-b156-c43d2ae1598d.png" 
            alt="DriveCourses Logo" 
            className="h-8 max-w-full"
          />
        </Link>
      </div>
      <nav className="flex-1 overflow-auto py-4">
        <div className="px-4 mb-4">
          <h2 className="mb-2 text-xs font-semibold text-muted-foreground">
            General
          </h2>
          <div className="space-y-1">
            <SidebarLink 
              href="/dashboard" 
              icon={<Home className="h-4 w-4" />}
              active={isActive('/dashboard')}
            >
              Dashboard
            </SidebarLink>
            <SidebarLink 
              href="/users" 
              icon={<Users className="h-4 w-4" />}
              active={isActive('/users')}
            >
              Users
            </SidebarLink>
            <SidebarLink 
              href="/documents" 
              icon={<FileText className="h-4 w-4" />}
              active={isActive('/documents')}
            >
              Documents
            </SidebarLink>
            <SidebarLink 
              href="/courses" 
              icon={<BookOpen className="h-4 w-4" />}
              active={isActive('/courses')}
            >
              Courses
            </SidebarLink>
            <SidebarLink 
              href="/reviews" 
              icon={<Star className="h-4 w-4" />}
              active={isActive('/reviews')}
            >
              Reviews
            </SidebarLink>
            <SidebarLink 
              href="/discounts" 
              icon={<Percent className="h-4 w-4" />}
              active={isActive('/discounts')}
            >
              Discounts
            </SidebarLink>
            <SidebarLink 
              href="/chatlogs" 
              icon={<MessageSquare className="h-4 w-4" />}
              active={isActive('/chatlogs')}
            >
              Chat Logs
            </SidebarLink>
            <SidebarLink 
              href="/email-templates" 
              icon={<Mail className="h-4 w-4" />}
              active={isActive('/email-templates')}
            >
              Email Templates
            </SidebarLink>
          </div>
        </div>
        
        <div className="px-4 mb-4">
          <h2 className="mb-2 text-xs font-semibold text-muted-foreground">
            Compliance
          </h2>
          <div className="space-y-1">
            <SidebarLink 
              href="/compliance" 
              icon={<ShieldCheck className="h-4 w-4" />}
              active={isActive('/compliance')}
            >
              Compliance Dashboard
            </SidebarLink>
            <SidebarLink 
              href="/certificates" 
              icon={<Award className="h-4 w-4" />}
              active={isActive('/certificates')}
            >
              Certificates
            </SidebarLink>
            <SidebarLink 
              href="/certificates/templates" 
              icon={<FileText className="h-4 w-4" />}
              active={isActive('/certificates/templates')}
            >
              Templates
            </SidebarLink>
            <SidebarLink 
              href="/certificates/reports" 
              icon={<BarChart3 className="h-4 w-4" />}
              active={isActive('/certificates/reports')}
            >
              Reports
            </SidebarLink>
          </div>
        </div>
        
        <div className="px-4">
          <h2 className="mb-2 text-xs font-semibold text-muted-foreground">
            Settings
          </h2>
          <div className="space-y-1">
            <SidebarLink 
              href="/settings" 
              icon={<Settings className="h-4 w-4" />}
              active={isActive('/settings')}
            >
              Settings
            </SidebarLink>
          </div>
        </div>
      </nav>
    </div>
  );
}
